// Backend integration point: connects to existing MediQR Supabase project
// Uses NEXT_PUBLIC_SUPABASE_URL and NEXT_PUBLIC_SUPABASE_ANON_KEY env vars
// RLS policies on all tables enforce agent permissions server-side

import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL || 'https://placeholder.supabase.co';
const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY || 'placeholder-anon-key';

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: false, // Agent sessions managed manually in localStorage
    autoRefreshToken: false,
  },
  global: {
    headers: {
      'x-application-name': 'mediqr-agent-pwa',
    },
  },
});

export type Database = {
  public: {
    Tables: {
      user_profiles: {
        Row: {
          id: string;
          full_name: string | null;
          first_name: string | null;
          last_name: string | null;
          phone: string | null;
          role: 'super_admin' | 'partner' | 'hmo' | 'hospital' | 'agent' | 'validation_officer' | 'union_admin';
          user_status: 'active' | 'inactive' | 'invited' | 'pending' | 'suspended' | 'deactivated';
          union_id: string | null;
          state_id: string | null;
          last_login_at: string | null;
        };
      };
      agents: {
        Row: {
          id: string;
          user_id: string;
          partner_id: string | null;
          is_active: boolean;
        };
      };
      md_unions: {
        Row: {
          id: string;
          union_name: string;
          logo_url: string | null;
          state_id: string | null;
          is_active: boolean;
        };
      };
      states: {
        Row: {
          id: string;
          state_name: string;
          state_code: string;
          is_active: boolean;
        };
      };
      subscription_plans: {
        Row: {
          id: string;
          plan_name: string;
          plan_type: 'Daily' | 'Monthly';
          selling_price: number;
          is_active: boolean;
        };
      };
      qr_codes: {
        Row: {
          id: string;
          reference_id: string;
          batch_id: string | null;
          union_id: string | null;
          state_id: string | null;
          plan_id: string | null;
          status: 'generated' | 'issued' | 'active' | 'expired' | 'cancelled';
          qr_data: string | null;
          subscriber_phone: string | null;
          subscriber_name: string | null;
          activation_date: string | null;
          expiry_date: string | null;
          created_by: string | null;
          qr_image_url: string | null;
        };
      };
      qr_batches: {
        Row: {
          id: string;
          batch_number: string;
          qr_type: string | null;
          union_id: string | null;
          state_id: string | null;
          plan_id: string | null;
          quantity: number;
          generated_count: number;
          status: 'queued' | 'generating' | 'completed' | 'failed';
          created_by: string | null;
        };
      };
      subscriptions: {
        Row: {
          id: string;
          subscription_id: string;
          phone_number: string | null;
          subscriber_name: string | null;
          activation_method: 'qr_code' | 'phone_number';
          qr_reference: string | null;
          qr_code_id: string | null;
          plan_id: string | null;
          plan_type: 'Daily' | 'Monthly' | null;
          union_id: string | null;
          state_id: string | null;
          status: 'generated' | 'issued' | 'active' | 'expired' | 'cancelled';
          activation_date: string | null;
          expiry_date: string | null;
          created_by: string | null;
        };
      };
      master_audit_logs: {
        Row: {
          id: string;
          actor_id: string | null;
          actor_email: string | null;
          action: string;
          module: string | null;
          record_id: string | null;
          record_label: string | null;
          previous_value: Record<string, unknown> | null;
          new_value: Record<string, unknown> | null;
          created_at: string;
        };
      };
    };
  };
};